import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Helmet } from "react-helmet-async";
import {
    Phone,
    Mail,
    User,
    Loader2,
    ExternalLink,
    Home,
    AlertCircle
} from "lucide-react";
import ListingCard from "./ListingCard";

/**
 * Fix over-encoded S3 URLs (e.g., %252540 → %2540 → %40 → @)
 */
const decodeS3Url = (url) => {
    if (!url || typeof url !== 'string') return url;
    let decoded = url;
    let prev = '';
    while (decoded !== prev && decoded.includes('%25')) {
        prev = decoded;
        try {
            decoded = decodeURIComponent(decoded);
        } catch (e) {
            break;
        }
    }
    return decoded;
};

/**
 * AgentPortfolioPage - Public page showing all listings from an agent
 * Route: /flow/agent/:agentSlug
 */
const AgentPortfolioPage = ({ agentSlug }) => {
    const [portfolio, setPortfolio] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Format phone number for display
    const formatPhoneNumber = (phone) => {
        if (!phone) return null;
        const cleaned = phone.replace(/\D/g, "");
        if (cleaned.length === 10) {
            return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
        }
        return phone;
    };

    useEffect(() => {
        const fetchPortfolio = async () => {
            setLoading(true);
            setError(null);

            try {
                const apiUrl = process.env.REACT_APP_API_BASE || (
                    window.location.hostname === "localhost"
                        ? "http://localhost:8000"
                        : "https://api.cloudmortgage.io"
                );

                console.log("[AgentPortfolioPage] Fetching portfolio for:", agentSlug);
                const response = await fetch(
                    `${apiUrl}/api/public/galleries/agent/${agentSlug}`
                );

                if (!response.ok) {
                    if (response.status === 404) {
                        setError("Agent not found");
                    } else {
                        setError("Failed to load portfolio");
                    }
                    setLoading(false);
                    return;
                }

                const data = await response.json();
                console.log("[AgentPortfolioPage] Portfolio data received:", data);
                setPortfolio(data);
            } catch (err) {
                console.error("[AgentPortfolioPage] Error fetching portfolio:", err);
                setError("Failed to load portfolio. Please try again.");
            }

            setLoading(false);
        };

        if (agentSlug) {
            fetchPortfolio();
        }
    }, [agentSlug]);

    // Loading state
    if (loading) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="w-12 h-12 text-slate-400 animate-spin mx-auto mb-4" />
                    <p className="text-slate-600">Loading portfolio...</p>
                </div>
            </div>
        );
    }

    // Error state
    if (error || !portfolio) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center">
                <div className="text-center max-w-md px-4">
                    <AlertCircle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <h1 className="text-2xl font-semibold text-slate-900 mb-2">
                        {error || "Portfolio Not Found"}
                    </h1>
                    <p className="text-slate-600 mb-6">
                        We couldn&apos;t find the agent portfolio you&apos;re looking for.
                    </p>
                    <a
                        href="/flow"
                        className="inline-flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-xl font-semibold hover:bg-slate-800 transition-colors"
                    >
                        <Home className="w-5 h-5" />
                        Go to Flow
                    </a>
                </div>
            </div>
        );
    }

    const { agent, listings, totalListings } = portfolio;

    // Decode agent photo URL (may be over-encoded S3 URL)
    const agentPhotoUrl = decodeS3Url(agent.photoUrl || agent.photo_url || agent.photo);

    return (
        <>
            <Helmet>
                <title>{agent.name ? `${agent.name} - Real Estate Agent` : "Agent Portfolio"} | CloudMortgage.io</title>
                <meta
                    name="description"
                    content={`View ${totalListings || 0} property listings from ${agent.name || "this agent"}. ${agent.title || ""} ${agent.company ? `at ${agent.company}` : ""}`}
                />
                <meta property="og:title" content={`${agent.name || "Agent"} - Real Estate Portfolio`} />
                <meta property="og:description" content={`${totalListings || 0} Active Listings`} />
                {agentPhotoUrl && <meta property="og:image" content={agentPhotoUrl} />}
            </Helmet>

            <div className="min-h-screen bg-slate-50">
                {/* Agent Header */}
                <header className="bg-white border-b border-slate-200 shadow-sm">
                    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8">
                        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
                            {/* Agent Photo */}
                            {agentPhotoUrl ? (
                                <img
                                    src={agentPhotoUrl}
                                    alt={agent.name}
                                    className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg"
                                />
                            ) : (
                                <div className="w-24 h-24 rounded-full bg-slate-200 flex items-center justify-center border-4 border-white shadow-lg">
                                    <User className="w-12 h-12 text-slate-400" />
                                </div>
                            )}

                            {/* Agent Info */}
                            <div className="text-center sm:text-left flex-1">
                                <h1 className="text-3xl font-bold text-slate-900">
                                    {agent.name || "Real Estate Agent"}
                                </h1>
                                {(agent.title || agent.company) && (
                                    <p className="text-lg text-slate-600 mt-1">
                                        {agent.title}
                                        {agent.title && agent.company && " | "}
                                        {agent.company}
                                    </p>
                                )}

                                {/* Contact Info */}
                                <div className="flex flex-wrap justify-center sm:justify-start gap-4 mt-3 text-slate-500">
                                    {agent.phone && (
                                        <a
                                            href={`tel:${agent.phone}`}
                                            className="flex items-center gap-1.5 hover:text-blue-600 transition-colors"
                                        >
                                            <Phone className="w-4 h-4" />
                                            {formatPhoneNumber(agent.phone)}
                                        </a>
                                    )}
                                    {agent.email && (
                                        <a
                                            href={`mailto:${agent.email}`}
                                            className="flex items-center gap-1.5 hover:text-blue-600 transition-colors"
                                        >
                                            <Mail className="w-4 h-4" />
                                            {agent.email}
                                        </a>
                                    )}
                                </div>

                                {/* Listing Count */}
                                <p className="text-sm text-slate-500 mt-3">
                                    {totalListings || 0} Active Listing{totalListings !== 1 ? "s" : ""}
                                </p>
                            </div>

                            {/* Brokerage Logo */}
                            {agent.brokerageLogo && (
                                <div className="hidden lg:block">
                                    <img
                                        src={agent.brokerageLogo}
                                        alt={agent.company || "Brokerage"}
                                        className="h-16 w-auto object-contain"
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </header>

                {/* Listings Grid */}
                <main className="mx-auto max-w-7xl px-4 sm:px-6 py-8">
                    <h2 className="text-2xl font-semibold text-slate-900 mb-6">
                        All Listings
                    </h2>

                    {listings && listings.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {listings.map((listing) => (
                                <ListingCard key={listing.id} listing={listing} />
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-16">
                            <Home className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                            <p className="text-slate-600">No listings available at this time.</p>
                        </div>
                    )}
                </main>

                {/* Contact Section */}
                <section className="bg-white border-t border-slate-200 py-12">
                    <div className="mx-auto max-w-3xl px-4 text-center">
                        <h2 className="text-2xl font-semibold text-slate-900 mb-4">
                            Interested in Working with {agent.name || "this Agent"}?
                        </h2>
                        <p className="text-slate-600 mb-6">
                            Get in touch to discuss your real estate needs.
                        </p>
                        <div className="flex flex-col sm:flex-row justify-center gap-4">
                            {agent.phone && (
                                <a
                                    href={`tel:${agent.phone}`}
                                    className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors"
                                >
                                    <Phone className="w-5 h-5" />
                                    Call Now
                                </a>
                            )}
                            {agent.email && (
                                <a
                                    href={`mailto:${agent.email}`}
                                    className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-slate-300 rounded-xl font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
                                >
                                    <Mail className="w-5 h-5" />
                                    Send Email
                                </a>
                            )}
                        </div>
                    </div>
                </section>

                {/* Footer */}
                <footer className="border-t border-slate-200 bg-slate-50">
                    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8">
                        <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
                            <span>Presented with</span>
                            <a
                                href="https://cloudmortgage.io/flow"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-slate-600 hover:text-slate-800 transition-colors"
                            >
                                Flow by Cloud Mortgage, Inc
                            </a>
                            <ExternalLink className="w-3.5 h-3.5" />
                        </div>
                    </div>
                </footer>
            </div>
        </>
    );
};

AgentPortfolioPage.propTypes = {
    agentSlug: PropTypes.string.isRequired
};

export default AgentPortfolioPage;
